import React, { FormEvent, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { formatMessage } from 'i18n/CurrentLocale';
import { FormattedMessage } from 'react-intl';
import { USER_LOCALES, preferredCountriesForTelephoneInput, defaultCountryForTelephoneInput, DEFAULT_USER_LOCALE } from 'myParkdUtils/enums';
import { usePrevious } from 'myParkdUtils/customHooks';
import { getErrorMessageKey } from 'myParkdUtils/errorRendering';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleUser } from '@fortawesome/free-solid-svg-icons';
import * as validation from 'myParkdUtils/validationHandling';
import SubmitButton from 'components/SubmitButton';
import placeholders from 'string-translations/placeholders';
import ErrorMessage from 'components/ErrorMessage';
import GlobalErrorBoundary from 'components/GlobalErrorBoundary';
import { ReduxState, UserDTO } from 'types';
import { userSlice } from 'features/user/userSlice';
import PhoneInputWithCountrySelect, { isValidPhoneNumber } from 'react-phone-number-input';
import { getPhoneCountryLabels } from 'myParkdUtils/phoneLocales';


const UserSettingsForm = (props: Props) => {
    const userGetStatus = props.userGetStatus;
    const userUpdateStatus = props.userUpdateStatus;
    const previousUserUpdateStatus = usePrevious(userUpdateStatus);

    const [user, setUser] = useState<UserDTO>({
        email: '',
        givenName: '',
        familyName: '',
        locale: DEFAULT_USER_LOCALE,
        phoneNumber: '',
    });

    const [phoneNumberIsValid, setPhoneNumberIsValid] = useState(true);
    const [showValidationErrors, setShowValidationErrors] = useState(false);
    const [formSubmitted, setFormSubmitted] = useState(false);

    useEffect(() => {
        props.getUser();
    }, []);

    useEffect(() => {
        if (userGetStatus.succeeded) {
            setInitialStateValues();
        }
    }, [userGetStatus]);

    useEffect(() => {
        if (previousUserUpdateStatus && previousUserUpdateStatus.loading && (userUpdateStatus.succeeded || userUpdateStatus.error)) {
            setFormSubmitted(true);
        }
    }, [userUpdateStatus]);

    const setInitialStateValues = () => {
        setUser({
            email: props.user?.email || '',
            givenName: props.user?.givenName || '',
            familyName: props.user?.familyName || '',
            locale: props.user?.locale || DEFAULT_USER_LOCALE,
            phoneNumber: props.user?.phoneNumber || '',
        });
    };

    const handleInput = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        setUser({
            ...user,
            [e.target.name]: e.target.value,
        });
    };

    const handlePhoneInput = (phoneNumber: string) => {
        setUser({
            ...user,
            phoneNumber,
        });
        setPhoneNumberIsValid(isValidPhoneNumber(phoneNumber ?? ''));
    };

    const getValidationErrors = () => {
        let errors: ValidationErrors = {};
        errors.givenName = validation.checkIfNotNull(user.givenName);
        errors.familyName = validation.checkIfNotNull(user.familyName);
        errors.email = validation.checkIfValidEmailAddress(user.email);
        errors.phone = validation.checkIfPhoneIsValid(user.phoneNumber, phoneNumberIsValid);
        return errors;
    };

    const handleSubmit = (e: FormEvent) => {
        e.preventDefault();
        const validationSuccessful = validation.validate(getValidationErrors());
        if (validationSuccessful) {
            props.saveUser({ user });
        } else {
            setShowValidationErrors(true);
        }
    };

    const errors = getValidationErrors();
    const validationSuccessful = validation.validate(errors);

    return (
        <div className="radius-rectangle settings-box">
            <div className="settings-box-header">
                <h3 className="bold-title">
                    <FontAwesomeIcon icon={faCircleUser} className="icon-before" />
                    <FormattedMessage id="SETTINGS_PERSONAL_DETAILS" />
                </h3>
            </div>

            <GlobalErrorBoundary className="eb-red-background eb-rounded-corners-bottom">
                <div className="settings-box-content">
                    <form
                        id="userSettingsForm"
                        noValidate
                        onSubmit={(e) => handleSubmit(e)}
                        className={
                            'full-width-form ' +
                            (validationSuccessful ? 'validation-successful ' : '') +
                            (showValidationErrors ? 'show-validation-messages' : 'hide-validation-messages')
                        }
                    >
                        <div className="row-7">
                            <div className="col-6">
                                <label>
                                    <span>
                                        <FormattedMessage id="FIRST_NAME" />
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    className={'text-field ' + (errors.givenName ? 'error' : 'valid')}
                                    name="givenName"
                                    value={user.givenName}
                                    onChange={(e) => handleInput(e)}
                                    placeholder={formatMessage(placeholders.firstName)}
                                />
                                <ErrorMessage msg={errors.givenName} />
                            </div>
                            <div className="col-6">
                                <label>
                                    <span>
                                        <FormattedMessage id="LAST_NAME" />
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    className={'text-field ' + (errors.familyName ? 'error' : 'valid')}
                                    name="familyName"
                                    value={user.familyName}
                                    onChange={(e) => handleInput(e)}
                                    placeholder={formatMessage(placeholders.lastName)}
                                />
                                <ErrorMessage msg={errors.familyName} />
                            </div>
                        </div>

                        <div className="row-7">
                            <div className="col-6">
                                <label>
                                    <span>
                                        <FormattedMessage id="EMAIL" />
                                    </span>
                                </label>
                                <input
                                    type="email"
                                    className={'text-field ' + (errors.email ? 'error' : 'valid')}
                                    name="email"
                                    value={user.email}
                                    onChange={(e) => handleInput(e)}
                                    placeholder={formatMessage(placeholders.email)}
                                />
                                <ErrorMessage msg={errors.email} />
                            </div>
                            <div className="col-6">
                                <label htmlFor="inputPhone">
                                    <FormattedMessage id="PHONE" />
                                </label>
                                <div className={'phone-input-container ' + (errors.phone ? 'error' : 'valid')}>
                                    <PhoneInputWithCountrySelect
                                        id="inputPhone"
                                        name="phonenumber"
                                        international
                                        defaultCountry={defaultCountryForTelephoneInput}
                                        placeholder={formatMessage({ id: 'PLACEHOLDER_PHONE' })}
                                        value={user.phoneNumber}
                                        onChange={(value) => handlePhoneInput(value!)}
                                        labels={getPhoneCountryLabels(user.locale)}
                                    />
                                </div>
                                <ErrorMessage msg={errors?.phone} />
                            </div>
                        </div>

                        <div className="row-7">
                            <div className="col-6">
                                <label>
                                    <span>
                                        <FormattedMessage id="LANGUAGE" />
                                    </span>
                                </label>
                                <div className="select-wrap">
                                    <select value={user.locale} name="locale" onChange={(e) => handleInput(e)}>
                                        {USER_LOCALES.map((locale) => (
                                            <option value={locale} key={locale}>
                                                {formatMessage(placeholders[locale])}
                                            </option>
                                        ))}
                                        ;
                                    </select>
                                </div>
                            </div>
                        </div>

                        {formSubmitted && (
                            <React.Fragment>
                                {userUpdateStatus.error && (
                                    <div className="error-box">
                                        <FormattedMessage id={getErrorMessageKey(userUpdateStatus.error)} />
                                    </div>
                                )}
                                {userUpdateStatus.succeeded && (
                                    <div className="success-box">
                                        <FormattedMessage id="SETTINGS_PROFILE_UPDATE_SUCCESS" />
                                    </div>
                                )}
                            </React.Fragment>
                        )}

                        <SubmitButton buttonText={formatMessage({ id: 'SETTINGS_SAVE_PROFILE_DETAILS' })} saving={userUpdateStatus.loading} />
                    </form>
                </div>
            </GlobalErrorBoundary>
        </div>
    );
};

type ValidationErrors = {
    givenName?: any,
    familyName?: any,
    email?: any,
    phone?: any
}

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = StateProps & DispatchProps;

function mapStateToProps(state: ReduxState) {
    return {
        user: state?.user?.user,
        userGetStatus: state?.user?.userGetStatus,
        userUpdateStatus: state?.user?.userUpdateStatus,
        token: state.auth.token
    };
}

function mapDispatchToProps(dispatch: any) {
    return bindActionCreators({
        getUser: userSlice.actions.getUser,
        saveUser: userSlice.actions.saveUser,
    }, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(UserSettingsForm);